import { useEffect, useState } from "react";
import { Upload, Loader2, Users, Download } from "lucide-react";
import { api, errText } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { fmtDate, fmtNumber } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LdBadge } from "@/components/Badges";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function ImportPage() {
  const { customerId, current } = useCustomer();
  const { isManager } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unassigned, setUnassigned] = useState([]);
  const [people, setPeople] = useState([]);
  const [picked, setPicked] = useState([]);
  const [owner, setOwner] = useState("");

  const loadUnassigned = () =>
    api.get("/unassigned").then(({ data }) => setUnassigned(data.rows)).catch(() => {});

  useEffect(() => {
    loadUnassigned();
    api.get("/meta").then(({ data }) => setPeople(data.people)).catch(() => {});
  }, []);

  const choose = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/import/preview", form);
      setPreview(data);
      toast.success(`${data.count} row(s) ready. Nothing is saved yet.`);
    } catch (e) {
      toast.error(errText(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get("/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      const slug = (current?.name || "kanex").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      link.download = `${slug}-mis-import-template.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const commit = async () => {
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/import/commit", form);
      toast.success(`${data.imported} row(s) imported, ${data.unassigned} unassigned`);
      setPreview(null);
      setFile(null);
      await loadUnassigned();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!owner || picked.length === 0) {
      toast.error("Pick the rows and the person");
      return;
    }
    try {
      await api.post("/polines/bulk-assign", { ids: picked, owner });
      toast.success(`${picked.length} row(s) assigned to ${owner}`);
      setPicked([]);
      await loadUnassigned();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="import-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Import</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Bring the existing MIS in from CSV or Excel. Messy panel text is kept as it is, and the dropdown values are
          set from it.
        </p>
      </div>

      <div className="panel p-3 sm:p-4">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-secondary">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Choose a CSV or Excel file
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            data-testid="import-file-input"
            onChange={(e) => choose(e.target.files?.[0])}
          />
        </label>
        {file && <span className="ml-3 text-xs text-muted-foreground">{file.name}</span>}
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-button">
            <Download className="mr-2 h-4 w-4" />
            Download Excel template
          </Button>
          <p className="text-xs text-muted-foreground">
            One row per PO line. The columns are the PO block plus one block for every stage of{" "}
            <span className="font-medium" data-testid="import-template-customer">{current?.name || "this customer"}</span>, in workflow order.
          </p>
        </div>
      </div>

      {preview && (
        <div className="space-y-3" data-testid="import-preview">
          <div className="panel p-3 text-xs sm:p-4">
            <p>
              <span className="font-mono">{fmtNumber(preview.count)}</span> row(s) read ·{" "}
              <span className="font-mono">{fmtNumber(preview.unassigned)}</span> with no owner
            </p>
            {preview.unmappedColumns?.length > 0 && (
              <p className="mt-1 text-muted-foreground">
                Columns not recognised and skipped: {preview.unmappedColumns.join(", ")}
              </p>
            )}
            {preview.duplicates?.length > 0 && (
              <div
                className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                data-testid="import-duplicate-warning"
              >
                {preview.duplicates.length} PO{preview.duplicates.length === 1 ? "" : "s"} in this file
                {preview.duplicates.length === 1 ? " is" : " are"} already on the system for this customer:{" "}
                {preview.duplicates
                  .map((d) => `${d.poNumber}${d.branchCode ? ` (${d.branchCode})` : ""} · ${d.rows} row(s)`)
                  .join(", ")}
                . Importing will add them again.
              </div>
            )}
          </div>

          <div className="panel overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead className="border-b border-border bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {["PO number", "PO date", "Branch", "Item", "Qty", "Owner", "SO number", "Stage", "LD"].map((h) => (
                    <th key={h} className="cell font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {preview.rows.map((row, i) => (
                  <tr key={i} data-testid={`import-preview-row-${i}`}>
                    <td className="cell font-mono">{row.poNumber || "—"}</td>
                    <td className="cell font-mono">{fmtDate(row.poDate)}</td>
                    <td className="cell">{row.branchName || "—"}</td>
                    <td className="cell">{row.itemType || "—"}</td>
                    <td className="cell font-mono">{fmtNumber(row.qty)}</td>
                    <td className="cell">{row.owner || "Unassigned"}</td>
                    <td className="cell font-mono">{row.soNumber || "—"}</td>
                    <td className="cell">{row.derived.stageLabel}</td>
                    <td className="cell">
                      <LdBadge colour={row.derived.ldColour} text={row.derived.ldStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={commit} disabled={busy} data-testid="import-commit-button">
              Import {fmtNumber(preview.count)} row(s)
            </Button>
            <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }} data-testid="import-cancel-button">
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="panel p-3 sm:p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4" />
          Bulk assign unassigned rows
        </h2>
        {!isManager && (
          <p className="mt-1 text-xs text-muted-foreground">Only a manager can assign owners.</p>
        )}
        {unassigned.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="no-unassigned-text">
            Every line has an owner.
          </p>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div>
                <Label className="field-label">Assign to</Label>
                <select
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="bulk-assign-owner-select"
                >
                  <option value="">Pick a person</option>
                  {people.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <Button onClick={assign} disabled={!isManager} data-testid="bulk-assign-button">
                Assign {picked.length} row(s)
              </Button>
              <button
                className="text-xs text-muted-foreground hover:underline"
                onClick={() => setPicked(picked.length === unassigned.length ? [] : unassigned.map((r) => r.id))}
                data-testid="bulk-assign-select-all"
              >
                {picked.length === unassigned.length ? "Clear selection" : "Select all"}
              </button>
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[760px] text-left">
                <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="cell font-medium">Pick</th>
                    {["PO number", "PO date", "Branch", "Item", "Stage"].map((h) => (
                      <th key={h} className="cell font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {unassigned.map((row) => (
                    <tr key={row.id} data-testid={`unassigned-row-${row.id}`}>
                      <td className="cell">
                        <input
                          type="checkbox"
                          checked={picked.includes(row.id)}
                          onChange={(e) =>
                            setPicked((p) => (e.target.checked ? [...p, row.id] : p.filter((x) => x !== row.id)))
                          }
                          data-testid={`unassigned-check-${row.id}`}
                        />
                      </td>
                      <td className="cell font-mono">{row.poNumber}</td>
                      <td className="cell font-mono">{fmtDate(row.poDate)}</td>
                      <td className="cell">{row.branchName || "—"}</td>
                      <td className="cell">{row.itemType || "—"}</td>
                      <td className="cell">{row.derived.stageLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
